import { crmContactApi, leadWorkspaceApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const LEAD_WORKSPACE_ACTION_TYPE = createNamedActions('leadWorkspace', {
  GET_LEAD_APPLICATION_CONTACTS: 'GET_LEAD_APPLICATION_CONTACTS',
  GET_LEAD_SCORE: 'GET_LEAD_SCORE',
  RECOMPUTE_LEAD_SCORE: 'RECOMPUTE_LEAD_SCORE',
  ASSOCIATE_CONTACT: 'ASSOCIATE_CONTACT',
  MAKE_CONTACT_PRIMARY: 'MAKE_CONTACT_PRIMARY',
  UNLINK_CONTACT: 'UNLINK_CONTACT',
  CAPTURE_LEAD_SOURCE_CANDIDATE: 'CAPTURE_LEAD_SOURCE_CANDIDATE',
  GET_INDICATIVE_QUOTE: 'GET_INDICATIVE_QUOTE',
  GET_APPLICATION_PRICING_QUOTE: 'GET_APPLICATION_PRICING_QUOTE',
  GET_APPLICATION_PRICING_QUOTE_HISTORY: 'GET_APPLICATION_PRICING_QUOTE_HISTORY',
  RESET_LEAD_WORKSPACE: 'RESET_LEAD_WORKSPACE',
});

const normalizeAssociations = data => (Array.isArray(data) ? data : (data?.items ?? []));

const fetchLeadLinkAssociations = async ({ applicationId }) => {
  const { data } = await crmContactApi.getContactsByLead({ leadId: applicationId });

  return normalizeAssociations(data).map(association => ({ ...association, isLeadLink: true }));
};

export const getLeadApplicationContactsAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      const { data } = await leadWorkspaceApi.getApplicationContacts({ applicationId });
      const applicationAssociations = normalizeAssociations(data);
      const associations = applicationAssociations.length
        ? applicationAssociations
        : await fetchLeadLinkAssociations({ applicationId });

      const uniqueIds = [...new Set(associations.map(a => a.contactId).filter(Boolean))];
      const detailResults = await Promise.allSettled(
        uniqueIds.map(id => crmContactApi.getContactById(id).then(r => r.data)),
      );
      const detailMap = {};

      detailResults.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value) {
          detailMap[uniqueIds[idx]] = result.value;
        }
      });

      const enriched = associations.map(assoc => {
        const detail = detailMap[assoc.contactId] ?? {};

        return {
          ...detail,
          ...assoc,
          id: assoc.contactId ?? assoc.id ?? detail.id,
          firstName: assoc.firstName ?? detail.firstName ?? null,
          lastName: assoc.lastName ?? detail.lastName ?? null,
          primaryEmail: assoc.primaryEmail ?? detail.primaryEmail ?? null,
          primaryPhone: assoc.primaryPhone ?? detail.primaryPhone ?? null,
          contactType: assoc.contactType ?? detail.contactType ?? null,
          emails: assoc.emails ?? detail.emails ?? [],
          phones: assoc.phones ?? detail.phones ?? [],
        };
      });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.GET_LEAD_APPLICATION_CONTACTS,
        payload: enriched,
      });
    },
);

export const getLeadScoreAction = catchAsyncActionError(({ contactId }) => async dispatch => {
  const { data } = await leadWorkspaceApi.getLeadScore({ contactId });

  return dispatch({
    type: LEAD_WORKSPACE_ACTION_TYPE.GET_LEAD_SCORE,
    payload: data,
  });
});

export const recomputeLeadScoreAction = catchAsyncActionError(({ contactId }) => async dispatch => {
  const { data } = await leadWorkspaceApi.recomputeLeadScore({ contactId });

  return dispatch({
    type: LEAD_WORKSPACE_ACTION_TYPE.RECOMPUTE_LEAD_SCORE,
    payload: data,
  });
});

export const associateContactToApplicationAction = catchAsyncActionError(
  ({ contactId, applicationId, role }) =>
    async dispatch => {
      await leadWorkspaceApi.associateContact({ contactId, applicationId, role });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.ASSOCIATE_CONTACT,
        payload: { contactId, applicationId, role },
      });
    },
);

export const makeContactPrimaryAction = catchAsyncActionError(
  ({ contactId, applicationId }) =>
    async dispatch => {
      await leadWorkspaceApi.makeContactPrimary({ contactId, applicationId });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.MAKE_CONTACT_PRIMARY,
        payload: { contactId, applicationId },
      });
    },
);

export const unlinkContactFromLeadAction = catchAsyncActionError(
  ({ contactId, applicationId }) =>
    async dispatch => {
      await leadWorkspaceApi.unlinkContact({ contactId, applicationId });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.UNLINK_CONTACT,
        payload: { contactId, applicationId },
      });
    },
);

export const captureLeadSourceCandidateAction = catchAsyncActionError(
  ({ rawLabel }) =>
    async dispatch => {
      const { data } = await leadWorkspaceApi.captureLeadSourceCandidate({ rawLabel });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.CAPTURE_LEAD_SOURCE_CANDIDATE,
        payload: data,
      });
    },
);

export const getIndicativeQuoteAction = catchAsyncActionError(
  ({ applicationId, scenarioBody }) =>
    async dispatch => {
      const { data } = await leadWorkspaceApi.getIndicativeQuote({ applicationId, scenarioBody });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.GET_INDICATIVE_QUOTE,
        payload: data,
      });
    },
);

// Loads the latest saved Pricing Workbench quote for a lead. A 204 (no quote yet) comes back as an
// empty body, normalized to null so the Quoted Scenario card can render its empty state.
export const getApplicationPricingQuoteAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      const { data } = await leadWorkspaceApi.getApplicationPricingQuote({ applicationId });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.GET_APPLICATION_PRICING_QUOTE,
        payload: data || null,
      });
    },
);

// Loads the full append-only quote history for a lead, newest first (LEN-87). Always an array —
// [] when the lead has never been priced — so the Quoted Scenario card's history affordance stays
// hidden rather than erroring.
export const getApplicationPricingQuoteHistoryAction = catchAsyncActionError(
  ({ applicationId }) =>
    async dispatch => {
      const { data } = await leadWorkspaceApi.getApplicationPricingQuoteHistory({ applicationId });

      return dispatch({
        type: LEAD_WORKSPACE_ACTION_TYPE.GET_APPLICATION_PRICING_QUOTE_HISTORY,
        payload: Array.isArray(data) ? data : [],
      });
    },
);

export const resetLeadWorkspaceAction = () => ({
  type: LEAD_WORKSPACE_ACTION_TYPE.RESET_LEAD_WORKSPACE,
});
